import React, { useState } from 'react'
import { subscribe } from 'react-contextual'
import { useDispatch, useSelector } from 'react-redux'
import { useDebounce } from 'react-use'
import { get } from 'lodash-es'
import { AggregateTable, Button, CurrencyText, Header, Loader } from 'views/components'
import SettingIcon from 'components/atoms/Icons/Controls/Settings'
import { SearchFilterProvider } from 'views/providers'
import { formatStandard } from 'utils/date'
import { apiStateSelectors } from 'modules/api/apiState'
import { reportingApiEvents, reportingApiSelectors } from 'modules/api/reporting'
import { SUSPENSE_LIST_SEARCH_MAX_LENGTH } from 'modules/api/reporting/constants'
import { TSuspenseListKey, TSuspenseListSort } from 'modules/api/reporting/types'
import { $TSFixMe } from 'types/ts-migrate'
import SuspenseAccountsProvider from '../../SuspenseAccountsProvider'
import { SuspenseAccountsContext, SuspenseFilter, SuspenseReference } from '../../types'
import AllocationModal from '../Modal/AllocationModal/AllocationModal'
import SuspenseNote from '../SuspenseNote/SuspenseNote'
import SuspensePagination from '../SuspensePagination/SuspensePagination'
import SuspenseSummary from '../SuspenseSummary/SuspenseSummary'
import styles from './SuspenseAccountsTable.module.scss'

interface SuspenseAccountsTableProps {
  searchQuery?: string
  activeFilterIndex?: number
  filters?: SuspenseFilter[]
  openReference: (id: string) => void
  currentReference?: SuspenseReference | null
}

interface SearchFilterContext {
  searchQuery?: string
  activeFilterIndex?: number
}

const SEARCH_DEBOUNCE_MS = 300

/** Sorting is done by the API, so each sortable column's key is the API's `sort` value. */
const sortableBy = (key: TSuspenseListSort) => ({ enabled: true, order: false, firstOrder: 'desc', key })

const refCol = {
  Header: 'Reference used',
  accessor: 'referenceUsed',
  style: { flex: 2 },
}

const receivedCol = {
  Header: 'Received',
  accessor: 'dateReceived',
  sort: sortableBy('received'),
}

const allocatedToCol = {
  Header: 'Allocated to',
  accessor: 'allocatedToRef',
}

const allocatedAtCol = {
  Header: 'Allocated',
  accessor: 'allocatedAt',
  style: { flex: 1.5 },
  sort: sortableBy('allocated'),
}

const amountCol = {
  Header: 'Amount',
  accessor: 'amount',
  style: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingRight: '20px',
  },
  sort: sortableBy('amount'),
}

const ROW_HEIGHT = 80
/** A saved note has its "who · when" line under the input, which needs the extra room. */
const ROW_WITH_NOTE_HEIGHT = 90

const getRowHeight = (row: { hasNote?: boolean }): number => (row.hasNote ? ROW_WITH_NOTE_HEIGHT : ROW_HEIGHT)

const notesCol = {
  Header: 'Notes',
  accessor: 'notes',
  style: { flex: 2, paddingTop: 10, paddingBottom: 0 },
}

const actionCol = {
  Header: '',
  accessor: 'action',
  style: {
    display: 'flex',
    justifyContent: 'flex-end',
    maxWidth: '140px',
  },
}

const unallocatedColumns = [refCol, receivedCol, amountCol, notesCol, actionCol]
const allocatedColumns = [refCol, allocatedToCol, allocatedAtCol, amountCol]

const SuspenseAccountsTable = ({
  searchQuery,
  activeFilterIndex,
  filters,
  openReference,
  currentReference,
}: SuspenseAccountsTableProps): React.ReactElement => {
  const dispatch = useDispatch()
  const [allocatingTxId, setAllocatingTxId] = useState<string | null>(null)

  const filter = get(filters, `[${activeFilterIndex}]`, { data: [] }) as SuspenseFilter
  const listKey: TSuspenseListKey = filter.key === 'allocated' ? 'allocated' : 'unallocated'
  const isUnallocated = listKey === 'unallocated'

  const lists = useSelector(reportingApiSelectors.getSuspenseAccountLists)
  const { query, items, totalPages } = lists[listKey]
  const allocatingReference = lists.unallocated.items.find(item => item.txId === allocatingTxId)

  const isLoading = useSelector(state =>
    apiStateSelectors.isLoading(state)([reportingApiEvents.suspenseAccountList_request]),
  )

  const requestList = (
    list: TSuspenseListKey,
    changes: Parameters<typeof reportingApiEvents.suspenseAccountList_request>[0]['query'],
  ) => dispatch(reportingApiEvents.suspenseAccountList_request({ list, query: changes }))

  // Both lists are searched, so the tab counts show how many match on each.
  useDebounce(
    () => {
      const search = (searchQuery ?? '').trim().slice(0, SUSPENSE_LIST_SEARCH_MAX_LENGTH)
      ;(['unallocated', 'allocated'] as const).forEach(list => {
        if (lists[list].query.search !== search) {
          requestList(list, { search, page: 1 })
        }
      })
    },
    SEARCH_DEBOUNCE_MS,
    [searchQuery],
  )

  const referenceCell = (ref: SuspenseReference): React.ReactElement => (
    <span className={styles.stacked}>
      <strong>{ref.referenceUsed}</strong>
      <small>Transaction ID: {ref.txId?.substring(0, 9)}</small>
    </span>
  )

  const buildRow = (ref: SuspenseReference): $TSFixMe => {
    const shared = {
      ...ref,
      referenceUsed: referenceCell(ref),
      amount: (
        <strong>
          <CurrencyText>{ref.amount ?? 0}</CurrencyText>
        </strong>
      ),
      highlighted: currentReference?.txId === ref.txId,
    }
    const allocatedBy = ref.allocatedByName ?? ref.allocatedBy

    return isUnallocated
      ? {
          ...shared,
          dateReceived: formatStandard(ref.dateReceived),
          hasNote: !!ref.notes,
          notes: (
            <SuspenseNote
              txId={ref.txId}
              note={ref.notes}
              updatedBy={ref.notesUpdatedBy}
              updatedAt={ref.notesUpdatedAt}
            />
          ),
          action: (
            <Button
              size="sm"
              secondary
              onClick={(e: React.MouseEvent) => {
                // The row opens the detail panel on click; this skips it.
                e.stopPropagation()
                setAllocatingTxId(ref.txId ?? null)
              }}
            >
              Allocate
            </Button>
          ),
        }
      : {
          ...shared,
          allocatedToRef: (
            <span className={styles.stacked}>
              <strong>{ref.allocatedToRef}</strong>
              {ref.contactName && <small>{ref.contactName}</small>}
            </span>
          ),
          allocatedAt: (
            <span className={styles.stacked}>
              <strong>{formatStandard(ref.allocatedAt)}</strong>
              <small>
                {allocatedBy ? `by ${allocatedBy} · ` : ''}received {formatStandard(ref.dateReceived)}
              </small>
            </span>
          ),
        }
  }

  // Rows arrive in the API's order and are only built for the page on screen, so no memo is needed.
  const tableData = items.map(buildRow)

  const emptyMessage = query.search
    ? `No ${listKey} receipts match "${query.search}".`
    : `There are no ${listKey} receipts.`

  return (
    <div className={styles.root}>
      <Header icon={<SettingIcon />} text="Suspense Account" />
      <SuspenseSummary />
      {isLoading ? (
        <Loader />
      ) : tableData.length === 0 ? (
        <p className={styles.empty}>{emptyMessage}</p>
      ) : (
        <AggregateTable
          rowHeight={ROW_HEIGHT}
          getRowHeight={getRowHeight}
          columns={isUnallocated ? unallocatedColumns : allocatedColumns}
          data={tableData}
          interactive={true}
          onRowClick={row => openReference(row.txId)}
          sort={{ key: query.sort, order: query.order }}
          onSortChange={({ key, order }) => requestList(listKey, { sort: key as TSuspenseListSort, order, page: 1 })}
        />
      )}
      {/* Unallocated is loaded whole; only the allocated history is paged. */}
      {!isUnallocated && (
        <SuspensePagination
          page={query.page}
          totalPages={totalPages}
          disabled={isLoading}
          onPageChange={page => requestList(listKey, { page })}
        />
      )}
      <AllocationModal
        isOpen={!!allocatingReference}
        onClose={() => setAllocatingTxId(null)}
        currentReference={allocatingReference}
      />
    </div>
  )
}

export default subscribe(
  [SearchFilterProvider, SuspenseAccountsProvider],
  (
    { searchQuery, activeFilterIndex }: SearchFilterContext,
    { filters, openReference, currentReference }: SuspenseAccountsContext,
  ) => ({
    searchQuery,
    activeFilterIndex,
    filters,
    openReference,
    currentReference,
  }),
)(SuspenseAccountsTable)
